import React, { useState } from "react";
import {
  Heart,
  Building,
  MapPin,
  Phone,
  Mail,
  Globe,
  Users,
  Briefcase,
  User,
  Upload,
  FileText,
  CreditCard,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  X,
  Loader,
  Image,
} from "lucide-react";
import { registerLeadGenerator } from "../../services/leadGeneratorService";

const LeadGeneratorRegistration = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  // Form data state
  const [formData, setFormData] = useState({
    entityType: "", // New field for entity type
    entityName: "",
    cinNumber: "",
    gstNumber: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
    email: "",
    website: "",
    representativeName: "",
    representativeContact: "",
    representativeEmail: "",
    officialEmail: "",
    servicesRequired: [],
    accountEmail: "",
    password: "",
    confirmPassword: "",
  });

  // Documents state
  const [documents, setDocuments] = useState({
    // KYC Documents (All mandatory)
    cancelledCheque: null,
    adharCard: null,
    panTanCard: null,
    // Company Documentation (Optional)
    cinCertificate: null,
    tradeLicense: null,
    gstCertificate: null,
    otherProof: [], // Array for multiple files
    // MOU Document (Mandatory)
    mouDocument: null,
    // Entity Logo (Optional)
    entityLogo: null,
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  // Constants
  const steps = [
    { id: 1, title: "Entity Information", icon: Building },
    { id: 2, title: "Representative Details", icon: User },
    { id: 3, title: "Service Requirements", icon: Briefcase },
    { id: 4, title: "Documents", icon: FileText },
    { id: 5, title: "Account Setup", icon: CreditCard },
    { id: 6, title: "Review & Submit", icon: CheckCircle },
  ];

  const servicesOptions = [
    { label: "KYP (only)", value: 'KYP' },
    { label: "KYP + Discharge Flow", value: 'KYP_DF' },
    { label: "KYP + Discharge Flow + Finance", value: 'KYP_DF_F' },
    { label: "KYP + Discharge Flow + Finance + Settlement", value: 'KYP_DF_F_S' },
  ];

  const entityTypes = [
    "Company",
    "Individual as a consultant"
  ];

  const states = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const documentConfig = [
    // KYC Documents Section (All Mandatory)
    {
      section: "KYC Documents",
      description: "All KYC documents are mandatory for registration",
      documents: [
        {
          key: "cancelledCheque",
          label: "Cancelled Cheque",
          required: true,
          type: "document",
          description: "Bank cancelled cheque or bank statement"
        },
        {
          key: "adharCard",
          label: "Aadhaar Card",
          required: true,
          type: "document",
          description: "Aadhaar card of any director"
        },
        {
          key: "panTanCard",
          label: "PAN/TAN Card",
          required: true,
          type: "document",
          description: "PAN card or TAN certificate"
        }
      ]
    },
    // Company Documentation Section (Optional)
    {
      section: "Company Documentation",
      description: "Upload relevant company documents (Optional but recommended)",
      documents: [
        {
          key: "cinCertificate",
          label: "CIN Certificate",
          required: false,
          type: "document",
          description: "Certificate of Incorporation"
        },
        {
          key: "tradeLicense",
          label: "Trade License",
          required: false,
          type: "document",
          description: "Valid trade license"
        },
        {
          key: "gstCertificate",
          label: "GST Certificate",
          required: true,
          type: "document",
          description: "GST registration certificate"
        },
        {
          key: "otherProof",
          label: "Other Business Proof",
          required: false,
          type: "document",
          multiple: true,
          description: "Any other business registration proof (multiple files allowed)"
        }
      ]
    },
    // MOU Document Section (Mandatory)
    {
      section: "MOU Document",
      description: "Memorandum of Understanding (Mandatory)",
      documents: [
        {
          key: "mouDocument",
          label: "MOU Document",
          required: true,
          type: "document",
          description: "Signed MOU document"
        }
      ]
    },
    // Entity Logo Section (Optional)
    {
      section: "Entity Branding",
      description: "Upload your entity logo (Optional)",
      documents: [
        {
          key: "entityLogo",
          label: "Entity Logo",
          required: false,
          type: "image",
          description: "Company/entity logo in JPG, PNG format"
        }
      ]
    }
  ];

  // Event handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleMultiSelect = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [value],
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (docType, file) => {
    if (!file) return;

    // Validate file
  // Increase limit for cheque/bank statement to 100MB; others remain 5MB
  const isChequeOrStatement = docType?.toLowerCase()?.includes('cheque') || docType?.toLowerCase()?.includes('statement');
  const maxSize = isChequeOrStatement ? 100 * 1024 * 1024 : 5 * 1024 * 1024; // 100MB or 5MB
    const docItem = documentConfig
      .flatMap(section => section.documents)
      .find((doc) => doc.key === docType);
    const allowedTypes =
      docItem.type === "image"
        ? ["image/jpeg", "image/png", "image/jpg"]
        : ["application/pdf", "image/jpeg", "image/png", "image/jpg"];

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
  [docType]: `File size should be less than ${isChequeOrStatement ? '100MB' : '5MB'}`,
      }));
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      const typeText =
        docItem.type === "image" ? "JPEG, or PNG" : "PDF, JPEG, or PNG";
      setErrors((prev) => ({
        ...prev,
        [docType]: `Please upload ${typeText} files only`,
      }));
      return;
    }

    // Handle multiple files for otherProof
    if (docItem.multiple && docType === "otherProof") {
      setDocuments((prev) => ({ 
        ...prev, 
        [docType]: [...(prev[docType] || []), file] 
      }));
    } else {
      setDocuments((prev) => ({ ...prev, [docType]: file }));
    }
    
    setErrors((prev) => ({ ...prev, [docType]: "" }));

    // Simulate upload progress
    const progressKey = docItem.multiple ? `${docType}_${file.name}` : docType;
    setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        const progress = (prev[progressKey] || 0) + 10;
        if (progress >= 100) {
          clearInterval(interval);
          return { ...prev, [progressKey]: 100 };
        }
        return { ...prev, [progressKey]: progress };
      });
    }, 100);
  };

  const removeFile = (docType, fileName = null) => {
    const docItem = documentConfig
      .flatMap(section => section.documents)
      .find((doc) => doc.key === docType);
    
    if (docItem?.multiple && docType === "otherProof" && fileName) {
      // Remove specific file from array
      setDocuments((prev) => ({ 
        ...prev, 
        [docType]: prev[docType].filter(file => file.name !== fileName) 
      }));
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[`${docType}_${fileName}`];
        return newProgress;
      });
    } else {
      // Remove single file
      setDocuments((prev) => ({ ...prev, [docType]: null }));
      setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
    }
  };

  // Validation functions
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) =>
    /^[6-9]\d{9}$/.test(phone.replace(/\s+/g, ""));
  const validatePincode = (pincode) => /^\d{6}$/.test(pincode);
  const validateCIN = (cin) =>
    /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(cin);

  const validateStep = (step) => {
    const newErrors = {};
    const isCompany = formData.entityType === "Company";

    switch (step) {
      case 1: // Entity Information
        if (!formData.entityType) newErrors.entityType = "Entity type is required";
        if (!formData.entityName.trim())
          newErrors.entityName = isCompany ? "Company name is required" : "Name is required";
        
        // CIN and GST are only required for companies
        if (isCompany) {
          if (!formData.cinNumber.trim())
            newErrors.cinNumber = "CIN number is required";
          else if (!validateCIN(formData.cinNumber))
            newErrors.cinNumber = "Invalid CIN number format";
          if (!formData.gstNumber.trim())
            newErrors.gstNumber = "GST number is required";
        }
        
        if (!formData.address.trim()) newErrors.address = "Address is required";
        if (!formData.city.trim()) newErrors.city = "City is required";
        if (!formData.state) newErrors.state = "State is required";
        if (!formData.pincode.trim()) newErrors.pincode = "Pincode is required";
        else if (!validatePincode(formData.pincode))
          newErrors.pincode = "Invalid pincode format";
        if (!formData.phone.trim())
          newErrors.phone = "Phone number is required";
        else if (!validatePhone(formData.phone))
          newErrors.phone = "Invalid phone number";
        if (!formData.email.trim()) newErrors.email = "Email is required";
        else if (!validateEmail(formData.email))
          newErrors.email = "Invalid email format";
        break;

      case 2: // Representative Details
        if (!formData.representativeName.trim())
          newErrors.representativeName = "Representative name is required";
        if (!formData.representativeContact.trim())
          newErrors.representativeContact =
            "Representative contact is required";
        else if (!validatePhone(formData.representativeContact))
          newErrors.representativeContact = "Invalid contact number";
        if (!formData.representativeEmail.trim())
          newErrors.representativeEmail = "Representative email is required";
        else if (!validateEmail(formData.representativeEmail))
          newErrors.representativeEmail = "Invalid email format";
        if (!formData.officialEmail.trim())
          newErrors.officialEmail = "Official email is required";
        else if (!validateEmail(formData.officialEmail))
          newErrors.officialEmail = "Invalid email format";
        break;

      case 3: // Service Requirements
        if (formData.servicesRequired.length === 0)
          newErrors.servicesRequired = "Select at least one service";
        break;

      case 4: // Documents
        documentConfig.forEach((section) => {
          section.documents.forEach((doc) => {
            // For individual consultants, make company documentation optional
            if (!isCompany && section.section === "Company Documentation") {
              return; // Skip validation for company docs if individual
            }
            
            if (doc.required) {
              if (doc.multiple) {
                // For multiple file uploads, check if array has at least one file
                if (!documents[doc.key] || documents[doc.key].length === 0) {
                  newErrors[doc.key] = `${doc.label} is required`;
                }
              } else {
                // For single file uploads
                if (!documents[doc.key]) {
                  newErrors[doc.key] = `${doc.label} is required`;
                }
              }
            }
          });
        });
        break;

      case 5: // Account Setup
        if (!formData.accountEmail.trim())
          newErrors.accountEmail = "Account email is required";
        else if (!validateEmail(formData.accountEmail))
          newErrors.accountEmail = "Invalid email format";
        if (!formData.password) newErrors.password = "Password is required";
        else if (formData.password.length < 8)
          newErrors.password = "Password must be at least 8 characters";
        if (!formData.confirmPassword)
          newErrors.confirmPassword = "Confirm password is required";
        else if (formData.password !== formData.confirmPassword)
          newErrors.confirmPassword = "Passwords do not match";
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 6));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  // Submit
  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setLoading(true);
    try {
      const documentsToUpload = {};
      Object.entries(documents).forEach(([key, file]) => {
        if (file) {
          // Handle multiple files for otherProof
          if (key === "otherProof" && Array.isArray(file)) {
            if (file.length > 0) {
              // For multiple files, we'll flatten them with indexed keys
              file.forEach((singleFile, index) => {
                documentsToUpload[`${key}_${index}`] = singleFile;
              });
            }
          } else {
            documentsToUpload[key] = file;
          }
        }
      });

      const result = await registerLeadGenerator(formData, documentsToUpload);

      if (result.success) {
        setApplicationId(result.applicationId);
        setSubmitSuccess(true);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({ submit: "Registration failed. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Render components
  const renderFormField = (
    name,
    label,
    type = "text",
    required = true,
    placeholder = "",
    options = {}
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === "select" ? (
        <select
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors[name] ? "border-red-500" : "border-gray-300"
            }`}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : type === "textarea" ? (
        <textarea
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors[name] ? "border-red-500" : "border-gray-300"
            }`}
          placeholder={placeholder}
          rows={options.rows || 3}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors[name] ? "border-red-500" : "border-gray-300"
            }`}
          placeholder={placeholder}
          min={options.min}
        />
      )}
      {errors[name] && (
        <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
      )}
    </div>
  );

  const renderMultiSelect = (field, label, options, required = true) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <p className="text-sm text-gray-600 mb-3">
        Select all services that apply
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto bg-gray-50 p-4 rounded-lg">
        {options.map((option) => (
          <label
            key={option}
            className="flex items-center space-x-3 cursor-pointer bg-white p-3 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <input
              type="checkbox"
              checked={formData[field].includes(option.value)}
              onChange={() => handleMultiSelect(field, option.value)}
              className="rounded border-gray-300 text-blue-500 focus:ring-blue-500 h-4 w-4"
            />
            <span className="text-sm text-gray-700 font-medium">{option.label}</span>
          </label>
        ))}
      </div>
      <div className="mt-2 text-sm text-gray-600">
        Selected: {formData[field].length} service
        {formData[field].length !== 1 ? "s" : ""}
      </div>
      {errors[field] && (
        <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
      )}
    </div>
  );

  const renderPasswordField = (name, label, showState, setShowState) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <input
          type={showState ? "text" : "password"}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors[name] ? "border-red-500" : "border-gray-300"
            }`}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        <button
          type="button"
          onClick={() => setShowState(!showState)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center"
        >
          {showState ? (
            <EyeOff className="h-5 w-5 text-gray-400" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </div>
      {errors[name] && (
        <p className="text-red-500 text-sm mt-1">{errors[name]}</p>
      )}
    </div>
  );

  const renderDocumentUpload = (doc) => (
    <div key={doc.key} className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-medium text-gray-800 flex items-center">
            {doc.type === "image" ? (
              <Image className="h-5 w-5 mr-2 text-blue-500" />
            ) : (
              <FileText className="h-5 w-5 mr-2 text-blue-500" />
            )}
            {doc.label}{" "}
            {doc.required && <span className="text-red-500 ml-1">*</span>}
            {doc.multiple && <span className="text-blue-500 ml-1">(Multiple files allowed)</span>}
          </h4>
          <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
        </div>
        {doc.multiple && documents[doc.key] && documents[doc.key].length > 0 && (
          <div className="text-sm text-blue-600">
            {documents[doc.key].length} file{documents[doc.key].length !== 1 ? 's' : ''} uploaded
          </div>
        )}
        {!doc.multiple && documents[doc.key] && (
          <button
            onClick={() => removeFile(doc.key)}
            className="text-red-500 hover:text-red-700 p-2"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Upload Area */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors mb-4">
        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-sm text-gray-600 mb-2">
          Click to upload or drag and drop
        </p>
        <p className="text-xs text-gray-500">
          {doc.type === "image"
            ? "JPEG, PNG up to 5MB"
            : "PDF, JPEG, PNG up to 100MB"}
          {doc.multiple && " (You can upload multiple files)"}
        </p>
        <input
          type="file"
          accept={
            doc.type === "image" ? ".jpg,.jpeg,.png" : ".pdf,.jpg,.jpeg,.png"
          }
          multiple={doc.multiple}
          onChange={(e) => {
            if (doc.multiple) {
              // Handle multiple files
              Array.from(e.target.files).forEach(file => {
                handleFileUpload(doc.key, file);
              });
            } else {
              handleFileUpload(doc.key, e.target.files[0]);
            }
            // Reset input to allow same file upload again
            e.target.value = '';
          }}
          className="hidden"
          id={doc.key}
        />
        <label
          htmlFor={doc.key}
          className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer mt-4"
        >
          <Upload className="h-4 w-4 mr-2" />
          Choose File{doc.multiple ? 's' : ''}
        </label>
      </div>

      {/* Display uploaded files */}
      {doc.multiple && documents[doc.key] && documents[doc.key].length > 0 ? (
        <div className="space-y-3">
          <h5 className="font-medium text-gray-700">Uploaded Files:</h5>
          {documents[doc.key].map((file, index) => (
            <div key={`${file.name}_${index}`} className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {doc.type === "image" ? (
                    <Image className="h-5 w-5 text-green-600" />
                  ) : (
                    <FileText className="h-5 w-5 text-green-600" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-green-800">
                      {file.name}
                    </p>
                    <p className="text-xs text-green-600">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <button
                    onClick={() => removeFile(doc.key, file.name)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {uploadProgress[`${doc.key}_${file.name}`] && uploadProgress[`${doc.key}_${file.name}`] < 100 && (
                <div className="mt-3">
                  <div className="bg-green-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress[`${doc.key}_${file.name}`]}%` }}
                    />
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {uploadProgress[`${doc.key}_${file.name}`]}% uploaded
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : !doc.multiple && documents[doc.key] ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {doc.type === "image" ? (
                <Image className="h-5 w-5 text-green-600" />
              ) : (
                <FileText className="h-5 w-5 text-green-600" />
              )}
              <div>
                <p className="text-sm font-medium text-green-800">
                  {documents[doc.key].name}
                </p>
                <p className="text-xs text-green-600">
                  {(documents[doc.key].size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          {uploadProgress[doc.key] && uploadProgress[doc.key] < 100 && (
            <div className="mt-3">
              <div className="bg-green-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress[doc.key]}%` }}
                />
              </div>
              <p className="text-xs text-green-600 mt-1">
                {uploadProgress[doc.key]}% uploaded
              </p>
            </div>
          )}
        </div>
      ) : null}

      {errors[doc.key] && (
        <p className="text-red-500 text-sm mt-2">{errors[doc.key]}</p>
      )}
    </div>
  );

  const renderStepContent = () => {
    if (submitSuccess) {
      return (
        <div className="text-center space-y-6">
          <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              Registration Submitted Successfully!
            </h3>
            <p className="text-gray-600 mb-4">
              Your lead generator registration has been submitted for review.
            </p>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Application ID:</strong> {applicationId}
              </p>
              <p className="text-sm text-blue-600 mt-2">
                Please save this application ID for future reference. You will
                receive an email confirmation and further updates on your
                registered email address.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              <strong>What's Next?</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>
                • Our team will review your application within 2-3 business days
              </li>
              <li>• You'll receive an email notification once approved</li>
              <li>
                • After approval, you can login with your account credentials
              </li>
              <li>• Start accessing the selected services for your business</li>
            </ul>
          </div>

          <button
            onClick={() => (window.location.href = "/")}
            className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Return to Homepage
          </button>
        </div>
      );
    }

    switch (currentStep) {
      case 1: // Entity Information
        return (
          <div className="space-y-6">
            {/* Entity Type Selection */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">
                Registration Type
              </h4>
              <p className="text-sm text-blue-600">
                Select whether you're registering as a company or an individual consultant
              </p>
            </div>
            
            {renderFormField(
              "entityType",
              "Entity Type",
              "select",
              true,
              "Select entity type",
              entityTypes
            )}

            {/* Entity/Individual Name */}
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField(
                "entityName",
                formData.entityType === "Company" ? "Company Name" : "Full Name",
                "text",
                true,
                formData.entityType === "Company" ? "Enter company name" : "Enter your full name"
              )}
              
              {/* Show CIN only for companies */}
              {formData.entityType === "Company" && renderFormField(
                "cinNumber",
                "CIN Number",
                "text",
                true,
                "Enter CIN number"
              )}
            </div>

            {/* Show GST only for companies */}
            {formData.entityType === "Company" && renderFormField(
              "gstNumber",
              "GST Number",
              "text",
              true,
              "Enter GST number"
            )}

            {renderFormField(
              "address",
              "Address",
              "textarea",
              true,
              "Enter complete address",
              { rows: 3 }
            )}
            <div className="grid md:grid-cols-3 gap-6">
              {renderFormField("city", "City", "text", true, "Enter city")}
              {renderFormField(
                "state",
                "State",
                "select",
                true,
                "Select State",
                states
              )}
              {renderFormField("pincode", "Pincode", "text", true, "XXXXXX")}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField(
                "phone",
                "Phone Number",
                "tel",
                true,
                "+91 XXXXX XXXXX"
              )}
              {renderFormField(
                "email",
                "Email Address",
                "email",
                true,
                formData.entityType === "Company" ? "company@example.com" : "your@example.com"
              )}
            </div>
            {renderFormField(
              "website",
              "Website",
              "url",
              false,
              formData.entityType === "Company" ? "https://www.company.com" : "https://www.yoursite.com"
            )}
          </div>
        );

      case 2: // Representative Details
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">
                Representative Information
              </h4>
              <p className="text-sm text-blue-600">
                Primary contact person for this application
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField(
                "representativeName",
                "Representative Name",
                "text",
                true,
                "Full name"
              )}
              {renderFormField(
                "representativeContact",
                "Representative Contact",
                "tel",
                true,
                "+91 XXXXX XXXXX"
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {renderFormField(
                "representativeEmail",
                "Representative Email",
                "email",
                true,
                "representative@entity.com"
              )}
              {renderFormField(
                "officialEmail",
                "Official Email ID",
                "email",
                true,
                "official@entity.com"
              )}
            </div>
          </div>
        );

      case 3: // Service Requirements
        return (
          <div className="space-y-6">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <Briefcase className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Service Selection
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Select all the services your entity requires. You can modify
                    these later after approval.
                  </p>
                </div>
              </div>
            </div>
            {renderMultiSelect(
              "servicesRequired",
              "Services Required",
              servicesOptions
            )}
          </div>
        );

      case 4: // Documents
        return (
          <div className="space-y-8">
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Document Requirements
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    All documents must be clear, valid, and in the specified
                    format (max 100MB each for PDFs/images). Mandatory documents are marked with an asterisk (*).
                    {formData.entityType === "Individual as a consultant" && (
                      <span className="block mt-1 font-medium">
                        As an individual consultant, company documentation is optional.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {documentConfig.map((section, sectionIndex) => {
              // Hide company documentation for individual consultants
              if (formData.entityType === "Individual as a consultant" && 
                  section.section === "Company Documentation") {
                return (
                  <div key={sectionIndex} className="space-y-4">
                    <div className="p-4 rounded-lg border-l-4 bg-gray-50 border-gray-400">
                      <h3 className="font-semibold text-lg text-gray-600">
                        {section.section}
                        <span className="text-gray-500 ml-2">(Not applicable for individual consultants)</span>
                      </h3>
                      <p className="text-sm mt-1 text-gray-500">
                        This section is only required for company registrations.
                      </p>
                    </div>
                  </div>
                );
              }

              return (
                <div key={sectionIndex} className="space-y-4">
                  {/* Section Header */}
                  <div className={`p-4 rounded-lg border-l-4 ${
                    section.section === "KYC Documents" || section.section === "MOU Document"
                      ? "bg-red-50 border-red-400"
                      : "bg-blue-50 border-blue-400"
                  }`}>
                    <h3 className={`font-semibold text-lg ${
                      section.section === "KYC Documents" || section.section === "MOU Document"
                        ? "text-red-800"
                        : "text-blue-800"
                    }`}>
                      {section.section}
                      {(section.section === "KYC Documents" || section.section === "MOU Document") && (
                        <span className="text-red-500 ml-2">(Mandatory)</span>
                      )}
                      {(section.section === "Company Documentation" || section.section === "Entity Branding") && (
                        <span className="text-blue-500 ml-2">(Optional)</span>
                      )}
                    </h3>
                    <p className={`text-sm mt-1 ${
                      section.section === "KYC Documents" || section.section === "MOU Document"
                        ? "text-red-700"
                        : "text-blue-700"
                    }`}>
                      {section.description}
                    </p>
                  </div>

                  {/* Documents in this section */}
                  <div className="grid gap-6">
                    {section.documents.map(renderDocumentUpload)}
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 5: // Account Setup
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-2">
                Account Information
              </h4>
              <p className="text-sm text-blue-600">
                These credentials will be used to login after approval
              </p>
            </div>
            {renderFormField(
              "accountEmail",
              "User Email Id",
              "email",
              true,
              "login@entity.com"
            )}
            <div className="grid md:grid-cols-2 gap-6">
              {renderPasswordField(
                "password",
                "Password",
                showPassword,
                setShowPassword
              )}
              {renderPasswordField(
                "confirmPassword",
                "Confirm Password",
                showConfirmPassword,
                setShowConfirmPassword
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>Password Requirements:</strong> Minimum 8 characters
                with a combination of letters, numbers, and special characters
                recommended.
              </p>
            </div>
          </div>
        );

      case 6: // Review & Submit
        return (
          <div className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <div className="flex items-start space-x-3">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-800">
                    Review Your Information
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Please review all information before submitting your
                    application
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Entity Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Building className="h-5 w-5 mr-2 text-blue-500" />
                  Entity Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {formData.entityName}
                  </div>
                  <div>
                    <span className="font-medium">CIN:</span>{" "}
                    {formData.cinNumber}
                  </div>
                  <div>
                    <span className="font-medium">GST:</span>{" "}
                    {formData.gstNumber}
                  </div>
                  <div>
                    <span className="font-medium">Phone:</span> {formData.phone}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {formData.email}
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium">Address:</span>{" "}
                    {formData.address}, {formData.city}, {formData.state} -{" "}
                    {formData.pincode}
                  </div>
                  {formData.website && (
                    <div className="md:col-span-2">
                      <span className="font-medium">Website:</span>{" "}
                      {formData.website}
                    </div>
                  )}
                </div>
              </div>

              {/* Representative Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-500" />
                  Representative Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Name:</span>{" "}
                    {formData.representativeName}
                  </div>
                  <div>
                    <span className="font-medium">Contact:</span>{" "}
                    {formData.representativeContact}
                  </div>
                  <div>
                    <span className="font-medium">Email:</span>{" "}
                    {formData.representativeEmail}
                  </div>
                  <div>
                    <span className="font-medium">Official Email:</span>{" "}
                    {formData.officialEmail}
                  </div>
                </div>
              </div>

              {/* Services Required */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <Briefcase className="h-5 w-5 mr-2 text-blue-500" />
                  Services Required
                </h4>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                  {formData.servicesRequired.map((service) => (
                    <div key={service} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>{service}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-blue-500" />
                  Documents Uploaded
                </h4>
                <div className="space-y-4">
                  {documentConfig.map((section, sectionIndex) => {
                    const sectionDocs = section.documents.filter(doc => {
                      if (doc.multiple) {
                        return documents[doc.key] && documents[doc.key].length > 0;
                      }
                      return documents[doc.key];
                    });
                    if (sectionDocs.length === 0) return null;
                    
                    return (
                      <div key={sectionIndex} className="space-y-2">
                        <h5 className="font-medium text-gray-700 text-sm">{section.section}:</h5>
                        <div className="grid md:grid-cols-2 gap-2 text-sm pl-4">
                          {sectionDocs.map((doc) => (
                            <div key={doc.key} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <div>
                                <span>{doc.label}</span>
                                {doc.multiple && documents[doc.key] && documents[doc.key].length > 1 && (
                                  <span className="text-blue-600 ml-1">({documents[doc.key].length} files)</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Account Details */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-blue-500" />
                  Account Details
                </h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Account Email:</span>{" "}
                    {formData.accountEmail}
                  </div>
                  <div>
                    <span className="font-medium">Password:</span> ••••••••
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-700">{errors.submit}</p>
                </div>
              </div>
            )}

            {/* Terms & Conditions */}
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">
                    Terms & Conditions
                  </h4>
                  <p className="text-sm text-amber-700 mt-1">
                    By submitting this application, you agree to our terms and
                    conditions. Your application will be reviewed by our team
                    and you will be notified of the approval status via email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-3 rounded-xl">
              <Heart className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">SamHealth</h1>
              <p className="text-sm text-blue-600">
                Lead Generator Registration Portal
              </p>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Register as Lead Generator
          </h2>
          <p className="text-gray-600">
            Join our network and start offering healthcare services
          </p>
        </div>

        {/* Progress Steps */}
        {!submitSuccess && (
          <div className="mb-8">
            <div className="flex items-center justify-center">
              {steps.map((step, index) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isCompleted = currentStep > step.id;

                return (
                  <div key={step.id} className="flex items-center">
                    {/* Step Circle */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex items-center justify-center w-16 h-16 rounded-full border-2 transition-all duration-300 ${isActive
                          ? "bg-blue-500 border-blue-500 text-white shadow-lg"
                          : isCompleted
                            ? "bg-gray-100 border-gray-300 text-gray-400"
                            : "bg-gray-50 border-gray-200 text-gray-400"
                          }`}
                      >
                        <Icon className="h-7 w-7" />
                      </div>

                      {/* Step Label */}
                      <div className="mt-3 text-center max-w-24">
                        <p
                          className={`text-sm font-medium leading-tight ${isActive ? "text-blue-600" : "text-gray-500"
                            }`}
                        >
                          {step.title}
                        </p>
                      </div>
                    </div>

                    {/* Connector Line */}
                    {index < steps.length - 1 && (
                      <div
                        className={`h-0.5 w-16 mx-4 transition-colors duration-300 ${isCompleted ? "bg-blue-500" : "bg-gray-200"
                          }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Form Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        {!submitSuccess && (
          <div className="flex justify-between items-center">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${currentStep === 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                }`}
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Previous</span>
            </button>

            {/* Step indicator */}
            <div className="text-sm text-gray-500 hidden md:block">
              Step {currentStep} of {steps.length}
            </div>

            {currentStep < 6 ? (
              <button
                onClick={nextStep}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                <span>Next</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Submit Application</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 text-center">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Need Help?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Our support team is here to assist you with the registration
              process
            </p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-6">
              <a
                href="mailto:support@samriddhihealthcare.com"
                className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Mail className="h-4 w-4 mr-2" />
                support@samriddhihealthcare.com
              </a>
              <a
                href="tel:+91-1800-XXX-XXXX"
                className="flex items-center text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Phone className="h-4 w-4 mr-2" />
                +91 1800-XXX-XXXX
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            © 2024 SamHealth. All rights reserved. |
            <a href="#" className="hover:text-blue-600 ml-1">
              Privacy Policy
            </a>{" "}
            |
            <a href="#" className="hover:text-blue-600 ml-1">
              Terms of Service
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LeadGeneratorRegistration;
